import * as React from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
  container?: HTMLElement;
  children: React.ReactNode;
}

function resolvePortalMount(container?: HTMLElement): HTMLElement {
  if (container) return container;
  /** `ThemeProvider` sets `--ui-*` on `[data-ui-root]`; portaling to `body` drops those variables. */
  return document.querySelector<HTMLElement>('[data-ui-root]') ?? document.body;
}

/**
 * Renders `children` into `document.body` (or `container`) on the client.
 * When `container` is omitted, prefers **`[data-ui-root]`** (inside `ThemeProvider`) so CSS variables apply.
 * No deferred mount: floating layers need the DOM node on the first paint
 * so parent `useLayoutEffect` hooks can measure and position correctly.
 */
export const Portal: React.FC<PortalProps> = ({ container, children }) => {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(children, resolvePortalMount(container));
};

Portal.displayName = 'Portal';
